import { useCallback, useMemo } from 'react';
import { useController, useFormContext } from 'react-hook-form';

import { getFormError } from 'constants/formErrors';

import type { EntityOption, EntitySource } from 'data/entity-sources/types';
import { useEntityDefaultOptions, useEntityOptions } from 'data/entity-sources/useEntityOptions';

import { Option, TotemSelect } from 'components/TotemSelect';

/**
 * Le `hint` est une ligne secondaire construite par le serveur. `TotemSelect` n'a qu'une ligne par
 * option : on l'y accole plutôt que de la perdre. C'est la SEULE mise en forme faite côté front — le
 * libellé lui-même vient de l'`optionsConfig` du repository.
 */
const toSelectOption = ({ value, label, hint, disabled }: EntityOption): Option<string> => ({
    value,
    label: hint ? `${label} · ${hint}` : label,
    isDisabled: disabled ?? false,
});

type UltraSelectBaseProps = {
    /** Constante de module (`siteSource`, `userSource`…). Aucun composant intermédiaire à écrire. */
    source: EntitySource;
    label?: string;
    sublabel?: string;
    placeholder?: string;
    width?: string;
    disabled?: boolean;
    isClearable?: boolean;
    dataTest?: string;
    className?: string;
};

export type UltraSelectProps = UltraSelectBaseProps &
    (
        | { multiple?: false; value: string | null; onChange: (value: string | null) => void }
        | { multiple: true; value: string[]; onChange: (values: string[]) => void }
    );

/**
 * Le champ de sélection d'une collection. Même source que la puce de filtre de la DataTable — la
 * recherche serveur, le périmètre et le libellé sont décidés une seule fois, dans la source.
 *
 * Ce que cela règle par rapport aux treize `components/Select/*` d'aujourd'hui : une valeur
 * présélectionnée est toujours nommée (y compris en multi), la largeur et le libellé ne sont plus
 * codés en dur dans un composant par collection, et changer une collection de statique à dynamique
 * ne touche aucun site d'appel.
 */
export function UltraSelect({
    source,
    label,
    sublabel,
    placeholder,
    width,
    disabled = false,
    isClearable = true,
    dataTest,
    className,
    ...selection
}: UltraSelectProps) {
    const loadOptions = useEntityOptions(source);

    const selectedValues = useMemo(() => {
        if (selection.multiple) {
            return selection.value;
        }
        return selection.value ? [selection.value] : [];
    }, [selection.multiple, selection.value]);

    const { defaultOptions, isLoading } = useEntityDefaultOptions(loadOptions, selectedValues);

    const fetchOptions = useCallback(
        async (query: string): Promise<Option<string>[]> => {
            const options = await loadOptions(query, []);

            return options.map(toSelectOption);
        },
        [loadOptions],
    );

    const commonProps = {
        label: label ?? source.labels.singular,
        sublabel,
        placeholder: placeholder ?? `Rechercher un ${source.labels.singular.toLowerCase()}…`,
        width,
        disabled: disabled || isLoading,
        isClearable,
        dataTest: dataTest ?? `${source.key}-select`,
        className,
        fetchOptions,
        defaultOptions: defaultOptions.map(toSelectOption),
    };

    // Deux retours concrets plutôt qu'un objet de props partagé : c'est ce qui évite les `as` que le
    // typage single/multi de `TotemSelect` imposait jusqu'ici (cf. `Select/UserSelect`).
    if (selection.multiple) {
        return <TotemSelect<string> {...commonProps} isMulti value={selection.value} onChange={selection.onChange} />;
    }

    return <TotemSelect<string> {...commonProps} value={selection.value} onChange={selection.onChange} />;
}

type UltraSelectFieldProps = Omit<UltraSelectBaseProps, 'source'> & {
    source: EntitySource;
    name: string;
    required?: boolean;
    multiple?: boolean;
};

/**
 * `UltraSelect` branché sur react-hook-form. Remplace `DetailFormSelect` dès que les options viennent
 * d'une collection : la source porte la recherche, le périmètre et le libellé, donc la section de
 * formulaire n'a plus ni requête ni `.map()`.
 */
export function UltraSelectField({
    name,
    source,
    required = false,
    multiple = false,
    label,
    ...props
}: UltraSelectFieldProps) {
    const {
        control,
        formState: { errors },
    } = useFormContext();
    const {
        field: { value, onChange },
    } = useController({ name, control, rules: { required } });

    const error = required && errors?.[name] ? getFormError(errors[name]?.type?.toString()) : undefined;

    return (
        <div className="relative w-full">
            {multiple ? (
                <UltraSelect
                    {...props}
                    source={source}
                    label={label}
                    multiple
                    value={Array.isArray(value) ? value : []}
                    onChange={onChange}
                />
            ) : (
                <UltraSelect {...props} source={source} label={label} value={value ?? null} onChange={onChange} />
            )}
            {error ? (
                <div className="absolute right-[-2px] top-[-16px] w-max text-base text-destructive">{error}</div>
            ) : null}
        </div>
    );
}
